import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';

import { COLORS } from './constants';

/* Register Chart.js building blocks once for the whole page. */
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

type Numeric = number | null | undefined;
type NumericList = Numeric[] | null | undefined;
type LabelList = (string | null | undefined)[] | null | undefined;

const toNumbers = (values?: NumericList): (number | null)[] =>
  (values ?? []).map((v) => (v === null || v === undefined ? null : v));

const maxTicks = (granularity: string): number => {
  if (granularity === 'day') {
    return 12;
  }
  return granularity === 'week' ? 14 : 24;
};

const baseScales = (granularity: string): ChartOptions<'bar'>['scales'] => ({
  x: {
    grid: { display: false },
    ticks: {
      autoSkip: true,
      maxTicksLimit: maxTicks(granularity),
      font: { size: 11 },
    },
  },
  y: {
    beginAtZero: true,
    ticks: { precision: 0, font: { size: 11 } },
    grid: { color: COLORS.grid },
  },
});

interface ISeriesProps {
  labels: LabelList;
  values: NumericList;
  color: string;
  granularity: string;
}

/** Vertical bar chart — volume series. */
export const BarChart = memo(
  ({ labels, values, color, granularity }: ISeriesProps) => {
    const options: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: baseScales(granularity),
    };

    return (
      <Bar
        options={options}
        data={{
          labels: labels as string[],
          datasets: [
            {
              data: toNumbers(values),
              backgroundColor: color,
              borderRadius: 4,
              maxBarThickness: 24,
            },
          ],
        }}
      />
    );
  },
);

/** Line chart with gap-spanning — median TTS over time. */
export const LineChart = memo(
  ({ labels, values, color, granularity }: ISeriesProps) => {
    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            autoSkip: true,
            maxTicksLimit: maxTicks(granularity),
            font: { size: 11 },
          },
        },
        y: {
          beginAtZero: true,
          ticks: { font: { size: 11 } },
          grid: { color: COLORS.grid },
        },
      },
    };

    return (
      <Line
        options={options}
        data={{
          labels: labels as string[],
          datasets: [
            {
              data: toNumbers(values),
              borderColor: color,
              backgroundColor: color,
              borderWidth: 2,
              tension: 0.25,
              spanGaps: true,
              pointRadius: 3,
              fill: false,
            },
          ],
        }}
      />
    );
  },
);

/** Static-bucket histogram — TTS distribution. */
export const HistogramChart = memo(
  ({ labels, values }: { labels: LabelList; values: NumericList }) => {
    const options: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, font: { size: 11 } },
          grid: { color: COLORS.grid },
        },
      },
    };

    return (
      <Bar
        options={options}
        data={{
          labels: labels as string[],
          datasets: [
            {
              data: toNumbers(values),
              backgroundColor: COLORS.tts,
              borderRadius: 4,
              maxBarThickness: 50,
            },
          ],
        }}
      />
    );
  },
);

/** Horizontal bar chart — average TTS per course. */
export const HorizontalBarChart = memo(
  ({ labels, values }: { labels: LabelList; values: NumericList }) => {
    const { t } = useTranslation();
    const options: ChartOptions<'bar'> = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { font: { size: 11 } },
          grid: { color: COLORS.grid },
          title: {
            display: true,
            text: t('pages.pandadocAnalytics.charts.daysToSignAxis'),
            font: { size: 11 },
            color: COLORS.axis,
          },
        },
        y: { grid: { display: false }, ticks: { font: { size: 12 } } },
      },
    };

    return (
      <Bar
        options={options}
        data={{
          labels: labels as string[],
          datasets: [
            {
              data: toNumbers(values),
              backgroundColor: COLORS.byCourse,
              borderRadius: 4,
              maxBarThickness: 32,
            },
          ],
        }}
      />
    );
  },
);

interface IStackProps {
  labels: LabelList;
  signed: NumericList;
  active: NumericList;
  lost: NumericList;
  horizontal?: boolean;
  granularity: string;
}

/** Stacked bar chart — sent→signed conversion. */
export const StackedBarChart = memo(
  ({ labels, signed, active, lost, horizontal, granularity }: IStackProps) => {
    const { t } = useTranslation();
    const signedNums = toNumbers(signed);
    const activeNums = toNumbers(active);
    const lostNums = toNumbers(lost);

    const options: ChartOptions<'bar'> = {
      indexAxis: horizontal ? 'y' : 'x',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            afterBody: (items) => {
              const idx = items[0].dataIndex;
              const total =
                (signedNums[idx] ?? 0) +
                (activeNums[idx] ?? 0) +
                (lostNums[idx] ?? 0);
              if (!total) {
                return '';
              }
              const conv = Math.round(((signedNums[idx] ?? 0) / total) * 100);
              return `${t('pages.pandadocAnalytics.charts.totalSent', {
                total,
              })}\n${t('pages.pandadocAnalytics.charts.conversionLine', {
                conv,
              })}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          grid: { display: !horizontal, color: COLORS.grid },
          ticks: {
            autoSkip: true,
            maxTicksLimit: maxTicks(granularity),
            font: { size: 11 },
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { display: !!horizontal, color: COLORS.grid },
          ticks: { precision: 0, font: { size: 11 } },
        },
      },
    };

    return (
      <Bar
        options={options}
        data={{
          labels: labels as string[],
          datasets: [
            {
              label: t('pages.pandadocAnalytics.charts.signed'),
              data: signedNums,
              backgroundColor: COLORS.signed,
              maxBarThickness: 28,
              borderRadius: 2,
            },
            {
              label: t('pages.pandadocAnalytics.charts.active'),
              data: activeNums,
              backgroundColor: COLORS.active,
              maxBarThickness: 28,
              borderRadius: 2,
            },
            {
              label: t('pages.pandadocAnalytics.charts.lost'),
              data: lostNums,
              backgroundColor: COLORS.lost,
              maxBarThickness: 28,
              borderRadius: 2,
            },
          ],
        }}
      />
    );
  },
);
